import api from './api';
import type {
  EstadoStock,
  EstadoVencimiento,
  Lectura,
  ResumenValorizacion,
} from '@suminuta/shared';

/**
 * Cliente de los reportes de inventario (PROMPT_N°054).
 *
 * Un solo módulo para los cinco: comparten la forma de pasar filtros y la
 * de bajar el Excel, y tenerlos juntos hace evidente cuándo dos reportes
 * deberían compartir un filtro.
 *
 * El Excel se pide como **blob con el token en el header**. Un
 * `window.open('/api/reportes/...')` sería más corto pero perdería el
 * `Authorization` y el backend devolvería 401 — el mismo motivo por el que
 * el CSV del kardex se baja así (N°053).
 */

export type NombreReporte =
  | 'existencias'
  | 'conciliacion'
  | 'lotes'
  | 'compras'
  | 'conteo';

export type Filtros = Record<string, string | number | boolean | undefined>;

/** Filtros a query string, salteando vacíos. */
export const filtrosAQuery = (f: Filtros): URLSearchParams => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
  });
  return p;
};

// ---------------------------------------------------------------- tipos

export interface FilaExistencia {
  id: number;
  nombre: string;
  marca: string | null;
  categoria: string | null;
  unidad: string;
  stock_actual: number;
  stock_minimo: number;
  estado: EstadoStock;
  /** Promedio ponderado de los lotes (N°055). */
  costo_promedio: number;
  /** El del catálogo; informativo, ya no valoriza. */
  costo_referencia: number | null;
  valor: number;
  stock_sin_lote: number;
  lotes_activos: number;
  proximo_vencimiento: string | null;
  dias_vencimiento: number | null;
  proveedor: string | null;
  sin_costo: boolean;
  activo: boolean;
}

export interface RespuestaExistencias {
  success: boolean;
  lectura: Lectura;
  valorizacion: ResumenValorizacion;
  filas: FilaExistencia[];
  porCategoria: { categoria: string; ingredientes: number; valor: number }[];
  total: { ingredientes: number; valor: number };
  sinCosto: { id: number; nombre: string; categoria: string | null }[];
}

export interface FilaConciliacion {
  id: number;
  nombre: string;
  categoria: string | null;
  unidad: string;
  saldo_inicial: number;
  e_factura: number;
  e_oc: number;
  e_bodega: number;
  e_reverso: number;
  entradas: number;
  s_bodega: number;
  s_produccion: number;
  s_merma: number;
  salidas: number;
  ajustes: number;
  saldo_final_calculado: number;
  saldo_sistema: number;
  diferencia: number;
  movimientos: number;
  valor_entradas: number;
  valor_salidas: number;
}

export interface RespuestaConciliacion {
  success: boolean;
  lectura: Lectura;
  filas: FilaConciliacion[];
  rango: { desde: string; hasta: string; offset: string };
  total: {
    ingredientes: number;
    con_diferencia: number;
    valor_entradas: number;
    valor_salidas: number;
  };
}

export interface FilaLote {
  id: number;
  ingrediente: string;
  categoria: string | null;
  numero_lote: string;
  proveedor: string | null;
  origen: string;
  fecha_ingreso: string;
  fecha_vencimiento: string | null;
  dias_restantes: number | null;
  semaforo: EstadoVencimiento;
  cantidad_inicial: number;
  disponible: number;
  unidad: string;
  costo_unitario: number;
  a_precio_referencia: boolean;
  valor_disponible: number;
}

export interface RespuestaLotes {
  success: boolean;
  lectura: Lectura;
  filas: FilaLote[];
  total: {
    lotes: number;
    valor_disponible: number;
    lotes_a_referencia: number;
    valor_vence_7: number;
    valor_vence_30: number;
    vencidos: number;
    valor_vencido: number;
  };
}

export interface RespuestaCompras {
  success: boolean;
  lectura: Lectura;
  rango: { desde: string; hasta: string };
  porProveedor: {
    proveedor_id: number | null;
    proveedor: string;
    facturas: number;
    neto: number;
    iva: number;
    total: number;
  }[];
  facturas: {
    id: number;
    numero: string;
    fecha: string;
    proveedor: string;
    neto: number;
    iva: number;
    total: number;
    lineas: number;
    estado: string;
  }[];
  lineas: {
    factura_id: number;
    ingrediente: string;
    cantidad: number;
    unidad: string;
    precio_unitario: number;
    subtotal: number;
    lote: string | null;
  }[];
  recepcionesSinFactura: {
    id: number;
    numero_orden: string;
    fecha: string | null;
    proveedor: string | null;
    estado: string;
    lineas: number;
    total: number;
  }[];
  total: { facturas: number; neto: number; iva: number; total: number };
}

export interface RespuestaConteo {
  success: boolean;
  lectura: Lectura;
  grupos: {
    categoria: string;
    filas: {
      id: number;
      nombre: string;
      marca: string | null;
      categoria: string;
      unidad: string;
      stock_sistema: number | null;
    }[];
  }[];
  total: number;
  mostrar_sistema: boolean;
}

// ---------------------------------------------------------------- cliente

const pedir = async <T>(reporte: NombreReporte, filtros: Filtros): Promise<T> => {
  const { data } = await api.get<T>(
    `/reportes/${reporte}?${filtrosAQuery(filtros).toString()}`
  );
  return data;
};

/** Sufijo del nombre de archivo: el rango si lo hay, si no la fecha de hoy. */
const sufijo = (filtros: Filtros): string => {
  if (filtros.desde && filtros.hasta) return `${filtros.desde}_${filtros.hasta}`;
  return new Date().toISOString().slice(0, 10);
};

export const reportesService = {
  existencias: (f: Filtros) => pedir<RespuestaExistencias>('existencias', f),
  conciliacion: (f: Filtros) => pedir<RespuestaConciliacion>('conciliacion', f),
  lotes: (f: Filtros) => pedir<RespuestaLotes>('lotes', f),
  compras: (f: Filtros) => pedir<RespuestaCompras>('compras', f),
  conteo: (f: Filtros) => pedir<RespuestaConteo>('conteo', f),

  descargarExcel: async (
    reporte: NombreReporte,
    filtros: Filtros
  ): Promise<void> => {
    const resp = await api.get(
      `/reportes/${reporte}/excel?${filtrosAQuery(filtros).toString()}`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reporte}_${sufijo(filtros)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default reportesService;
